/**
 * WEBSOCKET / SIGNALR ADMIN SERVICE: SECUENCIA (SIMON SAYS MULTIPLAYER)
 * Wrapper de comunicación en tiempo real para el Panel del Operador / Administrador del Estadio.
 * Gestiona la conexión persistente con SignalR Hub (`/hub/entertainment`) para recibir telemetría,
 * cambios de fase, aciertos/fallos en vivo, sincronización de la secuencia generada y control de portada.
 * 
 * @module utilities/WS/Admin/SequenceWS
 */

import SignalRService from '../SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes generales.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el panel de administración al Hub de SignalR con el rol de operador de Secuencia ('sequence:admin').
 * Se suscribe a la sala correspondiente (`room:{roomId}` o `screenId`) y despacha eventos a los callbacks registrados.
 * 
 * Eventos procesados:
 * - `sequence.layout`: Emisión de la secuencia aleatoria y configuración de botones generados para la partida.
 * - `sequence.update`: Telemetría en tiempo real de toques, avances de paso, estado WON o LOST.
 * - `sequence.reset`: Notificación de reinicio de la dinámica a estado 'WAITING'.
 * - `sequence.frontpage`: Notificación de proyección de portada en la pantalla gigante.
 * 
 * @param {Function} [onIncomingMessage=() => {}] - Callback general para procesar mensajes y eventos emitidos por el Hub.
 * @param {Function} [onStatusChange=() => {}] - Callback para notificar cambios de estado de conexión ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string|number} [roomId=''] - Identificador GUID o numérico de la sala / pantalla para filtrar el tráfico.
 * @param {Object} [eventCallbacks={}] - Callbacks específicos por tipo de evento.
 * @param {Function} [eventCallbacks.onLayout=null] - Callback específico para `sequence.layout`.
 * @param {Function} [eventCallbacks.onUpdate=null] - Callback específico para `sequence.update`.
 * @param {Function} [eventCallbacks.onReset=null] - Callback específico para `sequence.reset`.
 * @param {Function} [eventCallbacks.onFrontPage=null] - Callback específico para `sequence.frontpage`.
 */
export const ConnectWebSocketForSequenceAdmin = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = '',
    eventCallbacks = {}
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala / screenId
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        // Extraer roomId o screenId del mensaje envelope
        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.screenId
            || messageEnvelope.ScreenId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.payload?.screenId
            || messageEnvelope.payload?.ScreenId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.screenId;

        // Si se especificó roomId y el mensaje contiene identificador, validar aislamiento
        if (roomId && envelopeRoomId !== undefined && envelopeRoomId !== null) {
            if (String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
                return;
            }
        }

        const rawType = String(messageEnvelope.type || messageEnvelope.Type || '').toLowerCase();
        const payloadData = messageEnvelope.payload !== undefined ? messageEnvelope.payload : messageEnvelope.data;

        // Despacho a callbacks específicos según tipo de evento
        if (rawType === 'sequence.layout' && typeof eventCallbacks.onLayout === 'function') {
            try {
                eventCallbacks.onLayout(payloadData, messageEnvelope);
            } catch (err) {
                console.error('[SequenceAdminWS] Error en callback onLayout:', err);
            }
        } else if (rawType === 'sequence.update' && typeof eventCallbacks.onUpdate === 'function') {
            try {
                eventCallbacks.onUpdate(payloadData, messageEnvelope);
            } catch (err) {
                console.error('[SequenceAdminWS] Error en callback onUpdate:', err);
            }
        } else if (rawType === 'sequence.reset' && typeof eventCallbacks.onReset === 'function') {
            try {
                eventCallbacks.onReset(payloadData, messageEnvelope);
            } catch (err) {
                console.error('[SequenceAdminWS] Error en callback onReset:', err);
            }
        } else if (rawType === 'sequence.frontpage' && typeof eventCallbacks.onFrontPage === 'function') {
            try {
                eventCallbacks.onFrontPage(payloadData, messageEnvelope);
            } catch (err) {
                console.error('[SequenceAdminWS] Error en callback onFrontPage:', err);
            }
        }

        // Despachar también al handler general
        if (typeof onIncomingMessage === 'function') {
            try {
                onIncomingMessage(messageEnvelope);
            } catch (err) {
                console.error('[SequenceAdminWS] Error en onIncomingMessage general:', err);
            }
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'sequence:admin',
        roomId: roomId ? String(roomId) : '',
        screenId: roomId ? String(roomId) : ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[SequenceAdminWS] Error al establecer conexión con SignalR Hub:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del operador de Secuencia y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForSequenceAdmin = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[SequenceAdminWS] Error al desconectar la sesión de SignalR del operador:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForSequenceAdmin,
    DisconnectWebSocketForSequenceAdmin
};
